import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';
import { activeProfileHeader } from './viewerProfileService';

/**
 * Resume positions and the Continue watching row.
 *
 * Signed-in viewers only; every call acts on the caller's own positions.
 */

export interface WatchProgress {
  contentId: string;
  positionSeconds: number;
  durationSeconds: number;
  /** 0 to 1. */
  progress: number;
  completed: boolean;
  /** True when the player should offer to resume: far enough in, and not finished. */
  resumable: boolean;
  updatedAt: string;
}

export interface ContinueWatchingItem {
  content: {
    id: string;
    title: string;
    /** Needed for live-video content, which is addressed by slug, not id. */
    slug?: string | null;
    contentType: string;
    thumbnailUrl?: string | null;
    ageRating?: string | null;
  };
  progress: WatchProgress;
}

function apiUrl(path: string): string {
  const base = (config.apiBaseUrl || '').replace(/\/+$/, '');
  return base.endsWith('/api') ? `${base}/watch-progress${path}` : `${base}/api/watch-progress${path}`;
}

async function data<T>(response: Response): Promise<T> {
  const json = await response.json();
  return (json?.data ?? null) as T;
}

export async function getWatchProgress(contentId: string): Promise<WatchProgress | null> {
  // Which viewer is asking. Absent means the account's default profile, which is where
  // all history from before profiles existed lives.
  const response = await unifiedAuth.fetch(apiUrl(`/${encodeURIComponent(contentId)}`), {
    method: 'GET',
    headers: activeProfileHeader(),
  });
  if (!response.ok) return null;
  return data<WatchProgress | null>(response);
}

/**
 * @param keepalive set when the page is closing. A normal request is cancelled as the
 *   page unloads; keepalive lets it finish, which is the difference between keeping the
 *   position someone stopped at and losing the last stretch of what they watched.
 *   sendBeacon is not used because it cannot carry the Authorization header.
 */
export async function saveWatchProgress(
  contentId: string,
  positionSeconds: number,
  durationSeconds: number,
  keepalive = false,
): Promise<void> {
  await unifiedAuth.fetch(apiUrl(`/${encodeURIComponent(contentId)}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', ...activeProfileHeader() },
    body: JSON.stringify({
      positionSeconds: Math.max(0, Math.floor(positionSeconds)),
      durationSeconds: Math.max(1, Math.round(durationSeconds)),
    }),
    keepalive,
  });
}

export async function getContinueWatching(limit = 20): Promise<ContinueWatchingItem[]> {
  const response = await unifiedAuth.fetch(apiUrl(`/continue?limit=${limit}`), {
    method: 'GET',
    headers: activeProfileHeader(),
  });
  if (!response.ok) return [];
  return (await data<ContinueWatchingItem[]>(response)) ?? [];
}

export async function removeFromContinueWatching(contentId: string): Promise<void> {
  await unifiedAuth.fetch(apiUrl(`/${encodeURIComponent(contentId)}`), {
    method: 'DELETE',
    headers: activeProfileHeader(),
  });
}
